import React, { useEffect, useState } from 'react';
import { Check, Circle, Loader2, Gauge } from 'lucide-react';
import { getDriverVehicle, getDriverPaymentMethods, getDriverProfile } from '../../services/api';

const WEIGHTS = [
  { key: 'photo', label: 'Profile photo', weight: 20 },
  { key: 'bio', label: 'Short bio', weight: 15 },
  { key: 'insurance', label: 'Insurance declared', weight: 25 },
  { key: 'payments', label: 'Payment methods', weight: 15 },
  { key: 'vehicles', label: 'Vehicle added', weight: 15 },
  { key: 'areas', label: 'Service areas', weight: 10 },
];

/** How complete the driver's bidding profile is — nudges them to finish it. */
export default function ProfileStrengthCard({ hasPhoto, hasBio, insuranceDeclared }) {
  const [remote, setRemote] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  // The panels below fire this after a successful save so the meter keeps up.
  useEffect(() => {
    const bump = () => setRefreshKey((k) => k + 1);
    window.addEventListener('jd:profile-updated', bump);
    return () => window.removeEventListener('jd:profile-updated', bump);
  }, []);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const [v, p, prof] = await Promise.all([
          getDriverVehicle().catch(() => ({ data: {} })),
          getDriverPaymentMethods().catch(() => ({ data: {} })),
          getDriverProfile().catch(() => ({ data: {} })),
        ]);
        if (!active) return;
        setRemote({
          vehicles: (v.data?.vehicles || []).length > 0,
          payments: (p.data?.direct_payment_methods || p.data?.methods || []).length > 0,
          areas: (prof.data?.counties || []).length > 0 || !!prof.data?.serves_all_ireland || !!prof.data?.covers_all_ireland,
        });
      } catch {
        if (active) setRemote({ vehicles: false, payments: false, areas: false });
      }
    })();
    return () => { active = false; };
  }, [refreshKey]);

  const done = {
    photo: !!hasPhoto,
    bio: !!hasBio,
    insurance: !!insuranceDeclared,
    payments: !!remote?.payments,
    vehicles: !!remote?.vehicles,
    areas: !!remote?.areas,
  };
  const score = WEIGHTS.reduce((sum, i) => sum + (done[i.key] ? i.weight : 0), 0);
  const next = WEIGHTS.find((i) => !done[i.key]);

  return (
    <div
      className="rounded-xl border border-slate-200/80 bg-white shadow-sm overflow-hidden"
      data-testid="profile-strength-card"
    >
      <div className="px-5 py-4 bg-slate-50/60 border-b border-slate-100 flex items-center gap-3">
        <span className="p-2 rounded-lg bg-emerald-50 text-emerald-700 border border-emerald-100">
          <Gauge className="w-4 h-4" strokeWidth={2} />
        </span>
        <div className="flex-1 min-w-0">
          <p className="text-base font-semibold text-slate-800">Profile strength</p>
          <p className="text-xs text-slate-500">A complete profile wins more bids.</p>
        </div>
        <span className="text-xl font-extrabold text-[#1B5E20] tabular-nums" data-testid="profile-strength-percentage">
          {remote ? `${score}%` : <Loader2 className="w-4 h-4 animate-spin text-slate-300" />}
        </span>
      </div>

      <div className="p-5 space-y-4">
        <div className="h-2 w-full rounded-full bg-slate-100 overflow-hidden" data-testid="profile-strength-bar">
          <div
            className="h-full rounded-full bg-gradient-to-r from-[#2E7D32] to-[#1B5E20] transition-[width] duration-700 ease-out"
            style={{ width: `${remote ? score : 0}%` }}
          />
        </div>

        <ul className="space-y-1.5">
          {WEIGHTS.map((item) => (
            <li key={item.key} className="flex items-center gap-2 text-sm" data-testid={`strength-item-${item.key}`}>
              {done[item.key] ? (
                <span className="w-4 h-4 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center flex-shrink-0">
                  <Check className="w-2.5 h-2.5" strokeWidth={3.5} />
                </span>
              ) : (
                <Circle className="w-4 h-4 text-slate-300 flex-shrink-0" strokeWidth={2} />
              )}
              <span className={done[item.key] ? 'text-slate-500 line-through decoration-slate-300' : 'text-slate-700 font-medium'}>
                {item.label}
              </span>
            </li>
          ))}
        </ul>

        {next && remote && (
          <p className="text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 leading-snug"
            data-testid="profile-strength-next">
            Next up: add your <strong className="text-slate-800">{next.label.toLowerCase()}</strong> to reach{' '}
            {score + next.weight}%.
          </p>
        )}
        {!next && remote && (
          <p className="text-xs font-semibold text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2"
            data-testid="profile-strength-complete">
            Your profile is complete — nice work.
          </p>
        )}
      </div>
    </div>
  );
}
